import * as _ from 'underscore'
import { defaultFoldingExcludeList, XhprofCompactGraphCallExcludeList } from './XhprofCompactGraphFoldingExcludeList'
import { XhprofCallEdge, XhprofFlatInfoData } from './XhprofReportGenerator'

export class XhprofGraphCompactor {
    private foldingExcludeList: XhprofCompactGraphCallExcludeList

    constructor(foldingExcludeList: XhprofCompactGraphCallExcludeList = defaultFoldingExcludeList) {
        this.foldingExcludeList = foldingExcludeList
    }

    public compactGraph = (
        graph: XhprofFlatInfoData,
        threshold: number,
        dontFoldExcludeList: XhprofCompactGraphCallExcludeList = {},
    ): XhprofFlatInfoData => {
        const graphCopy: XhprofFlatInfoData = {}
        const foldingExcludeList = { ...this.foldingExcludeList, ...dontFoldExcludeList }

        // copy all nodes
        Object.keys(graph).forEach(call => {
            graphCopy[call] = {
                ...graph[call],
                parents: { ...graph[call].parents },
                children: { ...graph[call].children },
            }
        })

        while (true) {
            const nextChain = this.findFirstChain(graphCopy, threshold, foldingExcludeList)

            if (!nextChain) {
                break
            }

            this.collapseChain(graphCopy, nextChain)
        }

        return graphCopy
    }

    private findFirstChain = (
        graph: XhprofFlatInfoData,
        threshold: number,
        dontFoldExcludeList: XhprofCompactGraphCallExcludeList,
    ): Chain | undefined => {
        if (threshold < 50) {
            throw Error('threshold <50% does not make sense, more than one child could match this')
        }

        const edges: Edge[] = []
        const incomingEdgeCount: { [nodeId: string]: number } = {}
        const incomingEdgeIndex: { [nodeId: string]: number } = {}
        const outgoingEdgeIndex: { [nodeId: string]: number } = {}

        Object.keys(graph).forEach(call => {
            // our root should not be aggregated
            if (call === 'main()') {
                return
            }

            const currentThreshold = Math.abs((threshold / 100) * graph[call].wt)
            const callWalltime = Math.abs(graph[call].wt)

            let foundChild = false

            Object.keys(graph[call].children).forEach(child => {
                if (foundChild || child in dontFoldExcludeList) {
                    return
                }

                // recursive calls should not be collapsed
                if (child === call) {
                    return
                }

                if (!graph[child] || !graph[child].children) {
                    return
                }

                // leaves should never be collapsed
                if (Object.keys(graph[child].children).length === 0) {
                    return
                }

                const edgeWalltime = Math.abs(graph[call].children[child].wt)
                let { wt: childTotaltime, excl_wt: childSelftime } = graph[child]
                childTotaltime = Math.abs(childTotaltime)
                childSelftime = Math.abs(childSelftime)

                if (childTotaltime < currentThreshold || edgeWalltime < currentThreshold) {
                    return
                }

                // chain leading up to large node
                if (childTotaltime > callWalltime) {
                    return
                }

                // if a node spends almost all of its time by itself, it's
                // probably highly computational or an extension calling c-functions etc
                // and therefore important -> don't chain
                if (childSelftime / childTotaltime > 0.9) {
                    return
                }

                const edgeIndex = edges.push({ from: call, to: child }) - 1
                incomingEdgeCount[child] = (incomingEdgeCount[child] || 0) + 1

                if (incomingEdgeIndex[child] === undefined) {
                    incomingEdgeIndex[child] = edgeIndex
                }

                if (outgoingEdgeIndex[call] === undefined) {
                    outgoingEdgeIndex[call] = edgeIndex
                }

                // skip searching other children
                foundChild = true
            })
        })

        const visited = {}
        for (const [index, edge] of edges.entries()) {
            if (index in visited) {
                continue
            }

            let currentChain: Chain = { nodeIds: [edge.from, edge.to] }
            let current = edge

            // make sure our target node is not yet reserved for another chain
            if ((incomingEdgeCount[current.to] || 0) > 1) {
                continue
            }

            // go up as long as possible

            while (true) {
                const indexParent = incomingEdgeIndex[current.from]

                if (indexParent === undefined) {
                    break
                }

                visited[indexParent] = true
                currentChain = { nodeIds: [edges[indexParent].from, ...currentChain.nodeIds] }

                current = edges[indexParent]
            }

            current = edge
            // go down as long as possible

            while (true) {
                const indexChild = outgoingEdgeIndex[current.to]

                if (indexChild === undefined) {
                    break
                }

                visited[indexChild] = true

                currentChain = { nodeIds: [...currentChain.nodeIds, edges[indexChild].to] }

                current = edges[indexChild]
            }

            if (currentChain.nodeIds.length > 2) {
                return currentChain
            }
        }

        return undefined
    }

    private collapseChain = (graph: XhprofFlatInfoData, chain: Chain): void => {
        const { nodeIds } = chain

        if (nodeIds.length <= 2 || nodeIds.some(nodeId => graph[nodeId] === undefined)) {
            return
        }

        const [first, ...rest] = nodeIds
        // Preserve the full fold history by flattening already-folded descendants into the new head node.
        const foldedNodes = [...(graph[first].foldedNodes || []), ...rest.flatMap(nodeId => [nodeId, ...(graph[nodeId].foldedNodes || [])])]
        // Collect all outgoing edges that leave the chain and merge duplicates that now collapse onto the same child.
        const children = nodeIds.reduce<{ [child: string]: XhprofCallEdge }>((allChildren, nodeId) => {
            Object.keys(graph[nodeId].children).forEach(child => {
                if (nodeIds.indexOf(child) !== -1) {
                    return
                }

                allChildren[child] = this.mergeEdge(allChildren[child], graph[nodeId].children[child], child)
            })

            return allChildren
        }, {})

        graph[first] = {
            ...graph[first],
            // The folded head now owns the self-time of every node that disappeared into the chain.
            excl_wt: nodeIds.reduce((sumSelfTime, node) => sumSelfTime + graph[node].excl_wt, 0),
            children,
            foldedNodes,
        }

        rest.forEach(collapsedCall => {
            // Remove old parent -> collapsed child edges so no external node still points at a deleted chain node.
            Object.keys(graph[collapsedCall].parents).forEach(parent => {
                if (nodeIds.indexOf(parent) !== -1) {
                    return
                }

                if (!graph[parent] || !graph[parent].children) {
                    return
                }

                graph[parent].children = _.omit(graph[parent].children, collapsedCall)
            })

            // Reparent external children from the collapsed node to the surviving head node.
            Object.keys(graph[collapsedCall].children).forEach(child => {
                if (nodeIds.indexOf(child) !== -1) {
                    return
                }

                if (!graph[child] || !graph[child].parents) {
                    return
                }

                const { [collapsedCall]: collapsedParent, ...parents } = graph[child].parents
                // Keep parent links symmetric with the merged child map on `first`.
                graph[child].parents = {
                    ...parents,
                    [first]: this.mergeEdge(parents[first], collapsedParent, first),
                }
                graph[child].parents = collapsedParent ? { ...parents, [first]: { ...collapsedParent, name: first } } : parents
            })
        })

        // Only delete the tail nodes after all external references were rewritten to the surviving head node.
        rest.forEach(nodeId => delete graph[nodeId])
    }

    // Folding can create multiple parallel edges between the same two visible nodes; sum their metrics into one edge.
    private mergeEdge = (existing: XhprofCallEdge | undefined, next: XhprofCallEdge, name: string): XhprofCallEdge => ({
        name,
        ct: (existing?.ct || 0) + next.ct,
        wt: (existing?.wt || 0) + next.wt,
        pmu: (existing?.pmu || 0) + next.pmu,
    })
}

interface Chain {
    nodeIds: string[]
}

interface Edge {
    from: string
    to: string
}

export const xhprofCompactGraph = (
    graph: XhprofFlatInfoData,
    threshold: number,
    dontFoldExcludeList: XhprofCompactGraphCallExcludeList = {},
): XhprofFlatInfoData => {
    const compactor = new XhprofGraphCompactor()

    return compactor.compactGraph(graph, threshold, dontFoldExcludeList)
}
